import { placementName, placementsAt, type BoundDefinitions, type ProcessCatalogue, type StepSummary, type WorkflowDefinition } from "../../../api/content/processDefinitions";
import { FLATTENED_KEY_SEPARATOR } from "../../workflows/model/workflowGraph";
import type { LoadedProcess } from "../../workflows/data/processCatalogue";

/**
 * A step's name where the Thread shows a step (design review item 3: names, not keys).
 * Names come from the Thread's Workflow: by progress key (`prepare_subject.expand` for a
 * step of a sub-workflow) and by the step's exact version. Pure, so the lookup is tested.
 */
export interface StepNames {
  readonly byKey: ReadonlyMap<string, string>;
  readonly byReference: ReadonlyMap<string, string>;
}

export const NO_STEP_NAMES: StepNames = { byKey: new Map(), byReference: new Map() };

/**
 * Names in the reader's `language`: a step's translation of its name where its definition has one for that language or
 * its primary one ("de" for "de-DE"), else its own name (usability round 6: never a code name in German).
 */
export function stepNamesOf(process: LoadedProcess, language?: string): StepNames {
  const byKey = new Map<string, string>();
  const byReference = new Map<string, string>();
  for (const [reference, step] of process.catalogue.steps) {
    byReference.set(reference, translatedStepName(step, language));
  }
  collectKeys(process.workflow, null, process.catalogue, "", byKey, new Set(), language);
  return { byKey, byReference };
}

function translatedStepName(step: StepSummary, language: string | undefined): string {
  const translations = step.definition.translations;
  if (translations == null || language === undefined) return step.name;
  const name = (translations[language] ?? translations[language.split("-")[0] ?? language])?.name;
  return name === undefined || name === "" ? step.name : name;
}

/** Names by flat key, through the definitions each use binds (ADR-0043 D5), as the runner flattens them. */
function collectKeys(workflow: WorkflowDefinition, bound: BoundDefinitions | null, catalogue: ProcessCatalogue, prefix: string, byKey: Map<string, string>, seen: Set<string>, language: string | undefined): void {
  for (const use of placementsAt(workflow, bound, catalogue)) {
    const key = prefix + use.key;
    const step = use.kind === "step" ? catalogue.steps.get(use.step) : undefined;
    const name = step === undefined ? placementName(use, catalogue) : translatedStepName(step, language);
    if (name !== undefined) {
      byKey.set(key, name);
    }
    const inner = use.kind === "workflow" ? catalogue.workflows.get(use.workflow) : undefined;
    if (use.kind === "workflow" && inner !== undefined && !seen.has(use.workflow)) {
      collectKeys(inner, use.definitions, catalogue, key + FLATTENED_KEY_SEPARATOR, byKey, new Set([...seen, use.workflow]), language);
    }
  }
}

/**
 * The current Workflow's names, then those of the Workflows the Thread followed before (F126)
 * where the current one has none: an earlier entry names its step by its exact version, which
 * the current Workflow need not hold, and a step key it shares stays the current one's.
 */
export function withEarlierNames(current: StepNames, earlier: readonly StepNames[]): StepNames {
  const byKey = new Map<string, string>();
  const byReference = new Map<string, string>();
  for (const names of [...earlier].reverse()) {
    for (const [key, name] of names.byKey) byKey.set(key, name);
    for (const [reference, name] of names.byReference) byReference.set(reference, name);
  }
  for (const [key, name] of current.byKey) byKey.set(key, name);
  for (const [reference, name] of current.byReference) byReference.set(reference, name);
  return { byKey, byReference };
}

/** The step's name by its exact version, else by its progress key, else the key itself. */
export function nameOfStep(names: StepNames, stepKey: string | null, step: string | null = null): string {
  return (step === null ? undefined : names.byReference.get(step)) ?? (stepKey === null ? undefined : names.byKey.get(stepKey)) ?? stepKey ?? step ?? "";
}
